import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Modal, Table, Button, Form, Select, message, Popconfirm, Tag, Space, Card } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined, InfoCircleOutlined } from '@ant-design/icons';
import CodeEditor from 'components/shared/CodeEditor';
import templateService from 'services/templateService';

/**
 * OverrideManager - Manage organization and user-level prompt overrides
 * Supports dot-notation for nested property overrides
 */
const OverrideManager = ({ visible, onClose, template }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [overrides, setOverrides] = useState([]);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingOverride, setEditingOverride] = useState(null);
  const [overrideJson, setOverrideJson] = useState('{}');

  // Dropdown data
  const [organizations, setOrganizations] = useState([]);
  const [users, setUsers] = useState([]);
  const [pages, setPages] = useState([]);
  const [selectedScope, setSelectedScope] = useState('ORGANIZATION');

  useEffect(() => {
    if (visible && template?.templateId) {
      loadOverrides();
      loadOrganizations();
      loadUsers();
      loadPages();
    }
  }, [visible, template]);

  const loadOverrides = async () => {
    if (!template?.templateId) return;

    setLoading(true);
    try {
      const response = await templateService.getOverrides(template.templateId);
      if (response.success) {
        setOverrides(response.data || []);
      }
    } catch (error) {
      message.error('Failed to load overrides: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadOrganizations = async () => {
    try {
      // Assuming there's an organization service
      // For now, using placeholder data
      setOrganizations([
        { value: 'org1', label: 'Organization 1' },
        { value: 'org2', label: 'Organization 2' },
        { value: 'org3', label: 'Organization 3' },
      ]);
    } catch (error) {
      console.error('Failed to load organizations:', error);
    }
  };

  const loadUsers = async () => {
    try {
      // Assuming there's a user service
      // For now, using placeholder data
      setUsers([
        { value: 'user1', label: 'John Doe' },
        { value: 'user2', label: 'Jane Smith' },
        { value: 'user3', label: 'Bob Johnson' },
      ]);
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  };

  const loadPages = async () => {
    try {
      // These would ideally come from a page service
      setPages([
        { value: '08ab86f1-678a-4db5-9e6b-eeaff8dbef94', label: 'HRST / IM Combined incidents' },
        { value: '0f42c351-8ecb-40b8-991b-c0c533338cd9', label: 'Risk Categories' },
        { value: '7c9e1234-5678-90ab-cdef-1234567890ab', label: 'Individuals at Risk' },
        { value: '8d0f2345-6789-0abc-def1-234567890abc', label: 'Needs Level Analysis' },
      ]);
    } catch (error) {
      console.error('Failed to load pages:', error);
    }
  };

  const openCreateOverride = () => {
    setEditingOverride(null);
    form.resetFields();
    setSelectedScope('ORGANIZATION');
    setOverrideJson(getDefaultOverrideJson());
    setEditModalVisible(true);
  };

  const openEditOverride = (record) => {
    setEditingOverride(record);
    form.setFieldsValue({
      scope: record.scope,
      organizationId: record.organizationId,
      userId: record.userId,
      isActive: record.isActive,
    });
    setSelectedScope(record.scope);
    setOverrideJson(JSON.stringify(record.promptOverrides || {}, null, 2));
    setEditModalVisible(true);
  };

  const handleDeleteOverride = async (overrideId) => {
    try {
      await templateService.deleteOverride(template.templateId, overrideId);
      message.success('Override deleted successfully');
      loadOverrides();
    } catch (error) {
      message.error('Failed to delete override: ' + error.message);
    }
  };

  const handleSaveOverride = async () => {
    try {
      const values = await form.validateFields();

      // Validate JSON
      let parsedOverrides;
      try {
        parsedOverrides = JSON.parse(overrideJson);
      } catch (error) {
        message.error('Invalid JSON: ' + error.message);
        return;
      }

      const overrideData = {
        scope: values.scope,
        organizationId: values.scope === 'ORGANIZATION' ? values.organizationId : null,
        userId: values.scope === 'USER' ? values.userId : null,
        pageId: values.scope === 'PAGE' ? values.pageId : null,
        promptOverrides: parsedOverrides,
        isActive: values.isActive !== undefined ? values.isActive : true,
      };

      setLoading(true);

      if (editingOverride) {
        await templateService.updateOverride(
          template.templateId,
          editingOverride.overrideId,
          overrideData
        );
        message.success('Override updated successfully');
      } else {
        await templateService.createOverride(template.templateId, overrideData);
        message.success('Override created successfully');
      }

      setEditModalVisible(false);
      loadOverrides();
    } catch (error) {
      message.error('Failed to save override: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const getDefaultOverrideJson = () => {
    return JSON.stringify(
      {
        'basePrompts.system.role': 'Custom system role for this organization...',
        'basePrompts.analysis.types': ['custom', 'analysis'],
        'outputFormat.tone': 'custom_tone',
      },
      null,
      2
    );
  };

  const columns = [
    {
      title: 'Scope',
      dataIndex: 'scope',
      key: 'scope',
      render: (scope) => {
        const colorMap = {
          ORGANIZATION: 'orange',
          PAGE: 'blue',
          USER: 'green',
        };
        return <Tag color={colorMap[scope]}>{scope}</Tag>;
      },
    },
    {
      title: 'Organization',
      dataIndex: 'organizationName',
      key: 'organizationName',
      render: (text) => text || '-',
    },
    {
      title: 'Page',
      dataIndex: 'pageLabel',
      key: 'pageLabel',
      render: (text, record) => record.pageId ? (pages.find(p => p.value === record.pageId)?.label || record.pageId) : '-',
    },
    {
      title: 'User',
      dataIndex: 'userName',
      key: 'userName',
      render: (text) => text || '-',
    },
    {
      title: 'Overrides',
      dataIndex: 'promptOverrides',
      key: 'promptOverrides',
      render: (overrides) => {
        const count = Object.keys(overrides || {}).length;
        return <Tag color="blue">{count} properties</Tag>;
      },
    },
    {
      title: 'Created Date',
      dataIndex: 'createdDate',
      key: 'createdDate',
      render: (text) =>
        text
          ? new Date(text).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            })
          : '-',
    },
    {
      title: 'Active',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>{isActive ? 'Yes' : 'No'}</Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => openEditOverride(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Delete Override"
            description="Are you sure you want to delete this override?"
            onConfirm={() => handleDeleteOverride(record.overrideId)}
            okText="Yes"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Button size="small" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <StyledModal
        title={`Override Manager: ${template?.templateName || 'Template'}`}
        open={visible}
        onCancel={onClose}
        width={1200}
        footer={[
          <Button key="close" onClick={onClose}>
            Close
          </Button>,
        ]}
      >
        <Container>
          <Card
            size="small"
            extra={
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreateOverride}>
                Create Override
              </Button>
            }
          >
            <InfoSection>
              <InfoCircleOutlined style={{ color: '#1890ff' }} />
              <InfoText>
                Overrides allow you to customize prompts for specific organizations or users.
                Use dot-notation to override nested properties (e.g.,{' '}
                <code>"basePrompts.system.role"</code>).
              </InfoText>
            </InfoSection>

            <Table
              columns={columns}
              dataSource={overrides}
              rowKey="overrideId"
              loading={loading}
              pagination={{ pageSize: 10 }}
            />
          </Card>
        </Container>
      </StyledModal>

      {/* Override Editor Modal */}
      <Modal
        title={editingOverride ? 'Edit Override' : 'Create Override'}
        open={editModalVisible}
        onOk={handleSaveOverride}
        onCancel={() => setEditModalVisible(false)}
        confirmLoading={loading}
        width={900}
        okText={editingOverride ? 'Update' : 'Create'}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="Scope"
            name="scope"
            rules={[{ required: true, message: 'Please select scope' }]}
            initialValue="ORGANIZATION"
          >
            <Select onChange={setSelectedScope}>
              <Select.Option value="ORGANIZATION">Organization</Select.Option>
              <Select.Option value="PAGE">Specific Page</Select.Option>
              <Select.Option value="USER">User</Select.Option>
            </Select>
          </Form.Item>

          {selectedScope === 'ORGANIZATION' && (
            <Form.Item
              label="Organization"
              name="organizationId"
              rules={[{ required: true, message: 'Please select organization' }]}
            >
              <Select
                placeholder="Select organization"
                options={organizations}
                showSearch
                filterOption={(input, option) =>
                  option.label.toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>
          )}

          {selectedScope === 'PAGE' && (
            <Form.Item
              label="Application Page"
              name="pageId"
              rules={[{ required: true, message: 'Please select page' }]}
            >
              <Select
                placeholder="Select page for specific analysis"
                options={pages}
                showSearch
                filterOption={(input, option) =>
                  option.label.toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>
          )}

          {selectedScope === 'USER' && (
            <Form.Item
              label="User"
              name="userId"
              rules={[{ required: true, message: 'Please select user' }]}
            >
              <Select
                placeholder="Select user"
                options={users}
                showSearch
                filterOption={(input, option) =>
                  option.label.toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>
          )}

          <Form.Item label="Prompt Overrides (JSON)" required>
            <HelpText>
              <strong>Dot Notation Examples:</strong>
              <ul>
                <li>
                  <code>"basePrompts.system.role"</code>: Override system role
                </li>
                <li>
                  <code>"basePrompts.analysis.types"</code>: Override analysis types array
                </li>
                <li>
                  <code>"outputFormat.tone"</code>: Override output tone
                </li>
              </ul>
            </HelpText>
            <CodeEditor
              language="json"
              value={overrideJson}
              onChange={setOverrideJson}
              height="400px"
              theme="vs-dark"
            />
          </Form.Item>

          <Form.Item label="Active" name="isActive" valuePropName="checked" initialValue={true}>
            <Select defaultValue={true}>
              <Select.Option value={true}>Active</Select.Option>
              <Select.Option value={false}>Inactive</Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

const StyledModal = styled(Modal)`
  .ant-modal-body {
    padding: 24px;
  }
`;

const Container = styled.div`
  .ant-card {
    margin-bottom: 0;
  }
`;

const InfoSection = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 16px;
  background: #e6f7ff;
  border: 1px solid #91d5ff;
  border-radius: 4px;
  margin-bottom: 16px;
`;

const InfoText = styled.div`
  flex: 1;
  font-size: 14px;
  color: #595959;

  code {
    background: #f5f5f5;
    padding: 2px 6px;
    border-radius: 3px;
    font-family: 'Courier New', monospace;
    color: #d46b08;
  }
`;

const HelpText = styled.div`
  font-size: 12px;
  color: #595959;
  background: #fafafa;
  padding: 12px;
  border-radius: 4px;
  margin-bottom: 8px;

  strong {
    display: block;
    margin-bottom: 8px;
    color: #262626;
  }

  ul {
    margin: 0;
    padding-left: 20px;
  }

  li {
    margin-bottom: 4px;
  }

  code {
    background: #f0f0f0;
    padding: 2px 6px;
    border-radius: 3px;
    font-family: 'Courier New', monospace;
    color: #d46b08;
  }
`;

export default OverrideManager;
